import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { ShieldCheck, Quote, ArrowRight } from "lucide-react";

const MOTORWORLD_EXTERIOR = "/location/motorworld-exterior.webp";
const MOTORWORLD_INTERIOR = "/location/motorworld-interior.webp";
const MOTORWORLD_FACADE = "/location/motorworld-facade.webp";
const MOTORWORLD_HALL = "/location/motorworld-hall.webp";
const MOTORWORLD_ATRIUM = "/location/motorworld-atrium.webp";
const MOTORWORLD_CARS = "/location/motorworld-cars.webp";
const MOTORWORLD_SHOWROOM = "/location/motorworld-showroom.webp";

export default function About() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const statsList = (t.about.stats || []).map((s, i) => {
    if (!stats) return s;
    if (i === 0) return { ...s, value: `${stats.vehicles_total}+` };
    if (i === 1) return { ...s, value: `${stats.brands}` };
    return s;
  });

  const moreLink = {
    de: "Mehr über uns & Tim Tiedemann",
    en: "More about us & Tim Tiedemann",
    es: "Más sobre nosotros y Tim Tiedemann",
  }[lang] || "Mehr über uns";

  return (
    <section id="about" className="py-28 px-6 md:px-10 bg-obsidian-surface border-t border-white/10" data-testid="about-section">
      <div className="max-w-[1600px] mx-auto">
        <div>
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-4">{t.about.overline}</p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-5xl leading-[0.95] mb-6">
            {t.about.title}
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-5 max-w-2xl">{t.about.text}</p>
          <p className="text-zinc-400 leading-relaxed mb-8 max-w-2xl">{t.about.text2}</p>

          <div className="relative border-l-2 border-brand-light pl-6 mb-10 max-w-xl">
            <Quote className="w-6 h-6 text-brand-bright/40 mb-2" />
            <p className="font-heading text-white text-xl md:text-2xl leading-snug italic">{t.about.quote}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {statsList.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-obsidian p-5"
                data-testid={`about-stat-${i}`}
              >
                <div className="font-heading font-black text-brand-bright text-2xl md:text-3xl">{s.value}</div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>

          <Link
            to="/ueber-uns"
            data-testid="about-more-link"
            className="group inline-flex items-center gap-2 mt-8 border border-white/25 text-white font-medium uppercase tracking-wider text-xs px-6 py-3.5 hover:border-brand-light hover:text-brand-bright transition-colors"
          >
            {moreLink}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Showroom / MotorWorld Köln — Farb-Galerie (Architektur, 3 Bilder) */}
        <div className="mt-16" data-testid="about-showroom">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">{t.about.locationCaption}</p>
          <h3 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl mb-8 leading-[0.95]">
            MotorWorld Köln-Rheinland
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden border border-white/10 md:col-span-2 group aspect-[16/10]"
              data-testid="about-showroom-exterior"
            >
              <img
                src={MOTORWORLD_EXTERIOR}
                alt="MotorWorld Köln-Rheinland — Haupteingang"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 flex items-center gap-2 bg-black/60 backdrop-blur px-4 py-2 border border-brand-light/30">
                <ShieldCheck className="w-4 h-4 text-brand-bright" />
                <span className="text-xs uppercase tracking-widest text-white">Unser Standort · MotorWorld Köln</span>
              </div>
            </motion.div>

            {[
              { src: MOTORWORLD_FACADE, alt: "MotorWorld Köln — historische Fassade des Flughafens Butzweilerhof", caption: "Historische Fassade" },
              { src: MOTORWORLD_INTERIOR, alt: "MotorWorld Köln — Innenansicht des Fahrzeug-Turms", caption: "Fahrzeug-Turm" },
            ].map((img, i) => (
              <motion.div
                key={img.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden border border-white/10 aspect-[16/10] md:aspect-auto md:min-h-[240px] group"
                data-testid={`about-showroom-img-${i}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                <div className="absolute bottom-3 left-3 text-[10px] uppercase tracking-widest text-white bg-black/55 backdrop-blur px-2.5 py-1">
                  {img.caption}
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-zinc-500 text-xs mt-4 italic">
            Architekturbilder: MotorWorld Köln-Rheinland · Wikimedia Commons (CC BY / CC BY-SA)
          </p>
        </div>
      </div>
    </section>
  );
}
